import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getFolders, createFolder, updateFolder, deleteFolder } from '../api/folders';

export default function FoldersPage() {
  const qc                     = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [newName,  setNewName]  = useState('');
  const [createErr, setCreateErr] = useState('');

  const { data: folders = [], isLoading } = useQuery({
    queryKey: ['folders'],
    queryFn:  () => getFolders().then((r) => r.data),
  });

  const createMut = useMutation({
    mutationFn: (name) => createFolder({ name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders'] });
      setNewName('');
      setCreating(false);
      setCreateErr('');
    },
    onError: (err) => setCreateErr(err.response?.data?.error || 'Failed to create folder.'),
  });

  function handleCreate(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    createMut.mutate(newName.trim());
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Folders</h1>
        <button className="btn-primary text-sm" onClick={() => { setCreating(true); setCreateErr(''); }}>
          + New folder
        </button>
      </div>

      {/* Create form */}
      {creating && (
        <div className="card p-4">
          <form onSubmit={handleCreate} className="flex gap-2">
            <input
              autoFocus
              className="input flex-1"
              placeholder="Folder name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <button type="submit" className="btn-primary px-4" disabled={createMut.isPending}>
              {createMut.isPending ? '…' : 'Create'}
            </button>
            <button type="button" className="btn-secondary px-3" onClick={() => setCreating(false)}>
              Cancel
            </button>
          </form>
          {createErr && <p className="text-red-400 text-sm mt-2">{createErr}</p>}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <Spinner />
      ) : folders.length === 0 ? (
        <div className="card p-12 text-center space-y-2">
          <p className="text-4xl">📁</p>
          <p className="text-gray-300 font-medium">No folders yet</p>
          <p className="text-gray-500 text-sm">Create a folder to organise your images.</p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-800">
          {folders.map((folder) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              onSaved={() => qc.invalidateQueries({ queryKey: ['folders'] })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FolderRow({ folder, onSaved }) {
  const qc                      = useQueryClient();
  const [editing, setEditing]   = useState(false);
  const [name,    setName]      = useState(folder.name);
  const [err,     setErr]       = useState('');

  const renameMut = useMutation({
    mutationFn: (n) => updateFolder(folder.id, { name: n }),
    onSuccess: () => { onSaved(); setEditing(false); setErr(''); },
    onError:   (e) => setErr(e.response?.data?.error || 'Rename failed.'),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteFolder(folder.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders'] });
      qc.invalidateQueries({ queryKey: ['images'] });
    },
  });

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <span className="text-xl">📁</span>

      {editing ? (
        <form
          className="flex gap-2 flex-1"
          onSubmit={(e) => { e.preventDefault(); renameMut.mutate(name.trim()); }}
        >
          <input
            autoFocus
            className="input flex-1 text-sm py-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button type="submit" className="btn-primary text-xs px-3 py-1" disabled={renameMut.isPending}>
            Save
          </button>
          <button
            type="button"
            className="btn-secondary text-xs px-3 py-1"
            onClick={() => { setEditing(false); setName(folder.name); setErr(''); }}
          >
            Cancel
          </button>
          {err && <p className="text-red-400 text-xs self-center">{err}</p>}
        </form>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <Link
              to={`/dashboard?folder_id=${folder.id}`}
              className="font-medium text-white hover:text-brand-400 transition-colors truncate block"
            >
              {folder.name}
            </Link>
            <p className="text-xs text-gray-500">{folder.image_count} image{folder.image_count !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              className="text-xs text-gray-400 hover:text-white transition-colors"
              onClick={() => { setEditing(true); setName(folder.name); }}
            >
              Rename
            </button>
            <button
              className="text-xs text-red-400 hover:text-red-300 transition-colors"
              onClick={() => {
                if (confirm(`Delete folder "${folder.name}"? Images inside will be unassigned.`)) {
                  deleteMut.mutate();
                }
              }}
            >
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
